/**
 * Geocodificador especializado para infraestructuras hidráulicas MUNICIPALES
 * 
 * Complementa WFSHydraulicGeocoder (REDIAM) que solo cubre infraestructura regional:
 * - REDIAM: EDAR grandes, embalses, depósitos red autonómica (~850 total)
 * - Este: Depósitos municipales, pozos, manantiales locales (miles)
 * 
 * Cascada de fuentes (implementadas en T2-T4):
 * - L1: NGA - Nomenclátor Geográfico Andalucía (topónimos hídricos)
 * - L2: OSM Overpass (water_well, spring, water_tower)
 * - L3: Catastro por paraje/topónimo (centroide parcela)
 * 
 * Tipos cubiertos:
 * - Depósitos de agua municipales pequeños
 * - Pozos y sondeos de abastecimiento
 * - Manantiales y fuentes naturales
 * - Aljibes tradicionales
 * 
 * Creado: F3-HYDRAULIC T1 (2026-01-26)
 * @module services/geocoding/specialized
 */

import type { GeocodingResult } from '../../../types/infrastructure';
import { InfrastructureType } from '../../../types/infrastructure';
import { DERAHydraulicGeocoder, DERAHydraulicLayerType } from './DERAHydraulicGeocoder';
import { NGAGeocoder, ToponymType } from './NGAGeocoder';
import { OverpassGeocoder } from './OverpassGeocoder';
// F1-UNIFY-MIGRATE 9-may-2026: migrado de CatastroGeocoder legacy a V2 (R2 + uFuzzy + Flatbush, 2,4 M parcelas).
import { getCatastroGeocoderV2 } from './CatastroGeocoderV2';
import { getCodigoINE } from '../../../utils/codigosINEDerivados';

/**
 * Tipos de infraestructuras hidráulicas municipales
 *
 * Complementa `HydraulicFacilityType` de `WFSHydraulicGeocoder` que cubre
 * solo infraestructura regional REDIAM. Este enum agrupa entidades pequeñas
 * de gestión local: depósitos vecinales, pozos, manantiales locales, aljibes.
 *
 * **Decisión arquitectónica · DOP-HENUM-CONSUMER-1 (sub-DERA-HENUM 12-may-2026):**
 * NO incluye `PRESA`. Las presas son infraestructura **regional** inventariada
 * en CDAV g03_12 (n=592 Andalucía, agrupadas en `DERAHydraulicLayerType.PRESA`,
 * con `id_dera` único por presa). El enum municipal está diseñado para la
 * cascada L1-NGA → L2-OSM → L3-Catastro de entidades de pequeño tamaño no
 * inventariadas regionalmente. Si un PTEL municipal cataloga «presa» como
 * infraestructura propia, el detector `detectSubtype` no la reconoce
 * (`SUBTYPE_PATTERNS` no incluye `presa`) y cae al fallback `CAPTACION`, que
 * a su vez baja a `mapSubtypeToDERALayer` → `DERAHydraulicLayerType.CAPTACION`
 * (capa vacía en CDAV) y la cascada continúa por NGA/OSM/Catastro.
 *
 * Reabrir si aparece consumer municipal real que requiera categorizar «presa»
 * como subtipo propio: añadir aquí `PRESA` + caso explícito en
 * `mapSubtypeToDERALayer` y referenciar nuevo `DECISION_*` con la evidencia.
 *
 * Referencia persistente: `.ptel/DECISION_DERA_G03_CATALOGACION_20260512.md` §7.
 */
export enum MunicipalHydraulicType {
  /** Depósito de agua municipal (no autonómico) */
  DEPOSITO = 'DEPOSITO',
  /** Pozo o sondeo de captación */
  POZO = 'POZO',
  /** Manantial o fuente natural */
  MANANTIAL = 'MANANTIAL',
  /** Aljibe tradicional */
  ALJIBE = 'ALJIBE',
  /** Captación genérica (fallback) */
  CAPTACION = 'CAPTACION',
}

/**
 * Opciones de búsqueda para infraestructuras hidráulicas municipales
 */
export interface MunicipalHydraulicSearchOptions {
  /** Nombre de la infraestructura (ej: "Depósito Pozo Puntalillo") */
  name: string;
  /** Municipio de búsqueda */
  municipality: string;
  /** Provincia (opcional, mejora precisión) */
  province?: string;
  /** Código INE del municipio (5 dígitos) */
  codMun?: string;
  /** Subtipo específico (auto-detectado si no se proporciona) */
  subtype?: MunicipalHydraulicType;
  /** Paraje/topónimo extraído (opcional, auto-extraído si no se proporciona) */
  paraje?: string;
}

/**
 * Patrones regex para detectar subtipo de infraestructura hidráulica
 * Ordenados por especificidad (más específico primero)
 */
const SUBTYPE_PATTERNS: Array<{ pattern: RegExp; type: MunicipalHydraulicType }> = [
  // Depósitos - múltiples variantes
  { pattern: /\b(dep[oó]sito|tanque|balsa|cisterna)\b/i, type: MunicipalHydraulicType.DEPOSITO },
  // Pozos y sondeos
  { pattern: /\b(pozo|sondeo|perforaci[oó]n)\b/i, type: MunicipalHydraulicType.POZO },
  // Manantiales y fuentes
  { pattern: /\b(manantial|fuente|nacimiento|surgencia|naciente)\b/i, type: MunicipalHydraulicType.MANANTIAL },
  // Aljibes
  { pattern: /\b(aljibe|algibe)\b/i, type: MunicipalHydraulicType.ALJIBE },
];

/**
 * Prefijos comunes a eliminar para extraer topónimo
 * Ordenados por longitud (más largo primero para evitar matches parciales)
 */
const TOPONYM_PREFIXES = [
  // Prefijos compuestos con "junto" (más largos primero)
  /^dep[oó]sito\s+junto\s+(a(l)?\s+)?(manantial\s+)?/i,
  /^dep[oó]sito\s+junto\s+/i,
  // Prefijos compuestos con "pozo" dentro del nombre
  /^dep[oó]sito\s+pozo\s+(de(l)?\s+)?/i,
  /^dep[oó]sito\s+pozo\s+/i,
  // Depósito con artículo
  /^dep[oó]sito\s+de(l)?\s+/i,
  /^dep[oó]sito\s+/i,
  // Pozo con artículo
  /^pozo\s+de(l)?\s+/i,
  /^pozo\s+/i,
  // Sondeo con artículo
  /^sondeo\s+de(l)?\s+/i,
  /^sondeo\s+/i,
  // Manantial con artículo
  /^manantial\s+de(l)?\s+/i,
  /^manantial\s+/i,
  // Fuente con artículo
  /^fuente\s+de(l)?\s+/i,
  /^fuente\s+/i,
  // Aljibe con artículo
  /^aljibe\s+de(l)?\s+/i,
  /^aljibe\s+/i,
  // Tanque con artículo
  /^tanque\s+de(l)?\s+/i,
  /^tanque\s+/i,
  // Prefijos secundarios (para casos como "junto Lavadero")
  /^junto\s+(a(l)?\s+)?/i,
  /^cerca\s+(de(l)?\s+)?/i,
];

/**
 * Palabras que si quedan solas después de limpiar, indican que no hay topónimo real
 */
const COMMON_INFRASTRUCTURE_WORDS = [
  'pozo', 'deposito', 'depósito', 'manantial', 'fuente', 
  'sondeo', 'aljibe', 'tanque', 'de', 'del', 'la', 'el',
  'alto', 'bajo', 'nuevo', 'viejo', 'municipal',
];

/**
 * Geocodificador especializado para infraestructuras hidráulicas municipales
 * 
 * Usa cascada de fuentes complementarias a REDIAM:
 * L1: NGA (topónimos) → L2: OSM → L3: Catastro
 */
/**
 * Mapeo de subtipo hidráulico municipal → tipo topónimo NGA
 */
const SUBTYPE_TO_TOPONYM_TYPE: Partial<Record<MunicipalHydraulicType, ToponymType>> = {
  [MunicipalHydraulicType.MANANTIAL]: ToponymType.FUENTE,
  [MunicipalHydraulicType.ALJIBE]: ToponymType.FUENTE,
  // DEPOSITO y POZO no tienen equivalente directo en NGA
};

/**
 * Mapeo de subtipo hidráulico municipal → query OSM Overpass
 * @see https://wiki.openstreetmap.org/wiki/Tag:man_made%3Dwater_well
 */
const _SUBTYPE_TO_OSM_QUERY: Record<MunicipalHydraulicType, string[]> = {
  [MunicipalHydraulicType.POZO]: ['nwr["man_made"="water_well"]'],
  [MunicipalHydraulicType.MANANTIAL]: ['nwr["natural"="spring"]'],
  [MunicipalHydraulicType.DEPOSITO]: ['nwr["man_made"="water_tower"]', 'nwr["man_made"="storage_tank"]'],
  [MunicipalHydraulicType.ALJIBE]: ['nwr["man_made"="cistern"]', 'nwr["man_made"="water_well"]'],
  [MunicipalHydraulicType.CAPTACION]: ['nwr["man_made"="water_well"]', 'nwr["natural"="spring"]'],
};

export class HydraulicMunicipalGeocoder {
  private readonly minToponymLength = 3;
  private deraGeocoder: DERAHydraulicGeocoder | null = null;
  private ngaGeocoder: NGAGeocoder | null = null;
  private overpassGeocoder: OverpassGeocoder | null = null;
  // F1-UNIFY-MIGRATE 9-may-2026: el acceso a Catastro se hace ahora por singleton
  // (`getCatastroGeocoderV2()`); ya no requiere campo de instancia ni lazy init aquí.

  /** Obtiene o crea instancia de DERAHydraulicGeocoder (lazy init) */
  private getDERAGeocoder(): DERAHydraulicGeocoder {
    if (!this.deraGeocoder) {
      this.deraGeocoder = new DERAHydraulicGeocoder();
    }
    return this.deraGeocoder;
  }

  /** Obtiene o crea instancia de NGAGeocoder (lazy init) */
  private getNGAGeocoder(): NGAGeocoder {
    if (!this.ngaGeocoder) {
      this.ngaGeocoder = new NGAGeocoder();
    }
    return this.ngaGeocoder;
  }

  /** Obtiene o crea instancia de OverpassGeocoder (lazy init) */
  private getOverpassGeocoder(): OverpassGeocoder {
    if (!this.overpassGeocoder) {
      this.overpassGeocoder = new OverpassGeocoder();
    }
    return this.overpassGeocoder;
  }

  /**
   * Detecta el subtipo de infraestructura hidráulica por nombre
   * 
   * @param name - Nombre de la infraestructura
   * @returns Subtipo detectado (CAPTACION como fallback)
   * 
   * @example
   * detectSubtype("Depósito de Colomera") → DEPOSITO
   * detectSubtype("Pozo del Puntalillo") → POZO
   * detectSubtype("Manantial Cauro") → MANANTIAL
   * detectSubtype("Aljibe Municipal") → ALJIBE
   * detectSubtype("Captación Norte") → CAPTACION
   */
  detectSubtype(name: string): MunicipalHydraulicType {
    if (!name || typeof name !== 'string') {
      return MunicipalHydraulicType.CAPTACION;
    }

    const normalized = name.trim();
    
    for (const { pattern, type } of SUBTYPE_PATTERNS) {
      if (pattern.test(normalized)) {
        return type;
      }
    }
    
    return MunicipalHydraulicType.CAPTACION;
  }

  /**
   * Extrae el topónimo/paraje del nombre de infraestructura
   * 
   * Elimina prefijos comunes (depósito, pozo, etc.) para obtener
   * el nombre geográfico útil para búsqueda en NGA/Catastro.
   * 
   * @param name - Nombre completo de la infraestructura
   * @returns Topónimo extraído o null si no se puede extraer
   * 
   * @example
   * extractToponym("Depósito Pozo Puntalillo") → "Puntalillo"
   * extractToponym("Manantial Cauro") → "Cauro"
   * extractToponym("Depósito de Colomera") → "Colomera"
   * extractToponym("Pozo del Puntalillo") → "Puntalillo"
   * extractToponym("Depósito junto Lavadero") → "Lavadero"
   * extractToponym("Pozo") → null (muy corto)
   */
  extractToponym(name: string): string | null {
    if (!name || typeof name !== 'string') {
      return null;
    }

    // Normalizar espacios múltiples ANTES de aplicar prefijos
    let cleaned = name.trim().replace(/\s+/g, ' ');
    
    // Aplicar todos los prefijos para limpiar
    for (const prefix of TOPONYM_PREFIXES) {
      cleaned = cleaned.replace(prefix, '');
    }
    
    // Trim final
    cleaned = cleaned.trim();
    
    // Validar longitud mínima
    if (cleaned.length < this.minToponymLength) {
      return null;
    }
    
    // Verificar si el resultado es una palabra común de infraestructura
    const lowerCleaned = cleaned.toLowerCase();
    if (COMMON_INFRASTRUCTURE_WORDS.includes(lowerCleaned)) {
      return null;
    }
    
    return cleaned;
  }

  /**
   * Geocodifica una infraestructura hidráulica municipal
   * 
   * Cascada de fuentes (L0 → L3):
   * - L0: DERA G03 Hidrografía - Datos oficiales IECA (NUEVO)
   * - L1: NGA - Nomenclátor Geográfico Andalucía
   * - L2: OSM Overpass - OpenStreetMap
   * - L3: Catastro - Búsqueda por paraje/topónimo
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado de geocodificación o null si no encontrado
   */
  async geocode(options: MunicipalHydraulicSearchOptions): Promise<GeocodingResult | null> {
    const { name, municipality } = options;
    
    // Validación básica
    if (!name || !municipality) {
      return null;
    }
    
    // Auto-detectar subtipo si no se proporciona
    const subtype = options.subtype || this.detectSubtype(name);
    
    // Extraer topónimo para búsquedas
    const toponym = options.paraje || this.extractToponym(name);
    
    // === L0: DERA G03 Hidrografía (NUEVO - fuente oficial IECA) ===
    const deraResult = await this.searchDERA(name, options.codMun, subtype);
    if (deraResult) {
      return deraResult;
    }
    
    // === L1: NGA - Nomenclátor Geográfico Andalucía ===
    const ngaResult = await this.searchNGA(toponym, municipality, subtype);
    if (ngaResult) {
      return ngaResult;
    }
    
    // === L2: OSM Overpass ===
    const osmResult = await this.searchOSM(name, municipality, subtype);
    if (osmResult) {
      return osmResult;
    }
    
    // === L3: Catastro por paraje ===
    const catastroResult = await this.searchCatastro(toponym, municipality, options.codMun);
    if (catastroResult) {
      return catastroResult;
    }
    
    // Sin resultados en ninguna fuente
    return null;
  }

  /**
   * Busca en DERA G03 Hidrografía (manantiales, depósitos, captaciones)
   * 
   * Fuente oficial IECA con alta precisión (±5-15m).
   * Requiere codMun (código INE 5 dígitos) para búsqueda eficiente.
   * 
   * @param name - Nombre de la infraestructura
   * @param codMun - Código INE del municipio (5 dígitos)
   * @param subtype - Subtipo de infraestructura
   * @returns Resultado o null (confianza 85-95)
   * 
   * @internal Implementado en F3-HYDRAULIC DERA
   */
  protected async searchDERA(
    name: string,
    codMun: string | undefined,
    subtype: MunicipalHydraulicType
  ): Promise<GeocodingResult | null> {
    // DERA requiere código municipal para búsqueda eficiente
    if (!codMun) {
      return null;
    }

    const dera = this.getDERAGeocoder();

    // Mapear subtipo municipal → tipo capa DERA
    const deraLayerType = this.mapSubtypeToDERALayer(subtype);

    try {
      const result = await dera.geocodeWithAutoLayer({
        name,
        codMun,
        layerType: deraLayerType,
      });

      if (result && result.confidence >= 50) {
        // Ajustar confianza: DERA = 85-95 (fuente oficial más confiable)
        const adjustedConfidence = Math.min(95, Math.max(85, result.confidence));
        return {
          ...result,
          confidence: adjustedConfidence,
          source: 'DERA-HYDRAULIC',
          sourceLayer: result.sourceLayer || 'DERA:g03_hidrografia',
        };
      }
    } catch (error) {
      console.error('Error en searchDERA:', error);
    }

    return null;
  }

  /**
   * Mapea subtipo municipal a tipo de capa DERA
   */
  private mapSubtypeToDERALayer(subtype: MunicipalHydraulicType): DERAHydraulicLayerType {
    switch (subtype) {
      case MunicipalHydraulicType.MANANTIAL:
        return DERAHydraulicLayerType.MANANTIAL;
      case MunicipalHydraulicType.POZO:
      case MunicipalHydraulicType.CAPTACION:
        return DERAHydraulicLayerType.CAPTACION;
      case MunicipalHydraulicType.DEPOSITO:
      case MunicipalHydraulicType.ALJIBE:
      default:
        return DERAHydraulicLayerType.DEPOSITO;
    }
  }

  /**
   * Busca en NGA (Nomenclátor Geográfico Andalucía)
   * 
   * @param toponym - Topónimo a buscar
   * @param municipality - Municipio de búsqueda
   * @param subtype - Subtipo de infraestructura
   * @returns Resultado o null
   * 
   * @internal Implementación en T2
   */
  protected async searchNGA(
    toponym: string | null,
    municipality: string,
    subtype: MunicipalHydraulicType
  ): Promise<GeocodingResult | null> {
    if (!toponym) {
      return null;
    }

    const nga = this.getNGAGeocoder();
    const toponymType = SUBTYPE_TO_TOPONYM_TYPE[subtype] || ToponymType.FUENTE;

    try {
      const result = await nga.geocodeWithTypeDetection({
        name: toponym,
        municipality,
        toponymType,
      });

      if (result && result.confidence >= 50) {
        // Ajustar confianza: NGA para hídricos = 70-85
        const adjustedConfidence = Math.min(85, Math.max(70, result.confidence));
        return {
          ...result,
          confidence: adjustedConfidence,
          source: 'NGA-HYDRAULIC',
          sourceLayer: 'NGA:GN_NGA (topónimos hídricos)',
        };
      }
    } catch (error) {
      console.error('Error en searchNGA:', error);
    }

    return null;
  }

  /**
   * Busca en OSM Overpass con tags específicos hidráulicos
   * 
   * Tags OSM:
   * - POZO → man_made=water_well
   * - MANANTIAL → natural=spring
   * - DEPOSITO → man_made=water_tower | storage_tank
   * - ALJIBE → man_made=cistern | water_well
   * 
   * @param name - Nombre de la infraestructura
   * @param municipality - Municipio de búsqueda
   * @param subtype - Subtipo de infraestructura
   * @returns Resultado o null (confianza 65-80)
   * 
   * @internal Implementado en T3
   */
  protected async searchOSM(
    name: string,
    municipality: string,
    subtype: MunicipalHydraulicType
  ): Promise<GeocodingResult | null> {
    if (!name || !municipality) {
      return null;
    }

    const overpass = this.getOverpassGeocoder();

    try {
      const result = await overpass.geocode({
        name,
        municipality,
        infrastructureType: InfrastructureType.HYDRAULIC,
      });

      if (result && result.confidence >= 40) {
        // Ajustar confianza: OSM para hídricos = 65-80 (menor que NGA)
        const adjustedConfidence = Math.min(80, Math.max(65, result.confidence));
        return {
          ...result,
          confidence: adjustedConfidence,
          source: 'OSM-HYDRAULIC',
          sourceLayer: `OSM:Overpass (${subtype.toLowerCase()})`,
        };
      }
    } catch (error) {
      console.error('Error en searchOSM:', error);
    }

    return null;
  }

  /**
   * Busca en Catastro V2 (R2 + uFuzzy + Flatbush) por paraje/topónimo.
   *
   * F1-UNIFY-MIGRATE 9-may-2026 (DOP-P2/P3/P4): migrado del antiguo
   * `CatastroGeocoder.geocode` legacy a `getCatastroGeocoderV2().geocode()`.
   * - DOP-P2: `.geocode()` plain (sin OVC fallback) — Hydraulic ya tiene su
   *           propia cascada (L0-L3) y sumar OVC online aquí multiplicaría
   *           tráfico sin justificación.
   * - DOP-P3: V2 exige `codINE`; lo resolvemos desde el `codMun` propagado
   *           por el llamador (`geocode(...)`) o, como fallback, desde el
   *           nombre de municipio vía `getCodigoINE`.
   * - DOP-P4: ajuste de confianza `* 0.75` preservado — Catastro hídrico
   *           es señal débil, la penalización es del consumer, no del servicio.
   *
   * @param toponym - Topónimo/paraje a buscar.
   * @param municipality - Municipio de búsqueda.
   * @param codMun - Código INE del municipio (5 dígitos). Si no se propaga,
   *                 se intenta resolver desde `municipality`.
   * @returns Resultado o null (confianza ajustada 50-70).
   */
  protected async searchCatastro(
    toponym: string | null,
    municipality: string,
    codMun?: string,
  ): Promise<GeocodingResult | null> {
    if (!toponym || !municipality) {
      return null;
    }

    // V2 exige codINE — resolverlo desde codMun explícito o desde el nombre.
    const codINE = codMun || getCodigoINE(municipality);
    if (!codINE) {
      return null;
    }

    const catastro = getCatastroGeocoderV2();

    try {
      const result = await catastro.geocode({
        codINE,
        name: toponym,
        municipality,
      });

      if (result && result.confidence >= 30) {
        // DOP-P4: ajuste *0.75 preservado de la versión legacy.
        const adjustedConfidence = Math.min(70, Math.max(50, result.confidence * 0.75));
        return {
          ...result,
          confidence: adjustedConfidence,
          source: 'CATASTRO-HYDRAULIC',
          sourceLayer: 'Catastro:parcela (paraje)',
        };
      }
    } catch (error) {
      console.error('Error en searchCatastro:', error);
    }

    return null;
  }
}
